import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';
import styles from './servicos_montador_da_017.module.scss';

const WHATSAPP_URL =
    'https://wa.me/5517997146547?text=' +
    encodeURIComponent(
        'Olá, vim pelo Instagram e gostaria de um orçamento para montagem de móveis.'
    );

const VIDEO_SRC = require('./src/servicos/1.mp4') as string;

type Servico = {
    id: string;
    titulo: string;
    texto: string;
    alt: string;
    media: { kind: 'video'; src: string } | { kind: 'image'; src: string };
};

const SERVICOS: Servico[] = [
    {
        id: 'processo',
        titulo: 'Do chão da caixa ao móvel pronto',
        texto: 'Acompanhe o processo de quem monta todos os dias. Organização, técnica e atenção aos detalhes para o seu móvel ficar firme, alinhado e com cara de recém-saído da loja.',
        alt: 'Montagem de móveis em andamento, Montador da 017',
        media: { kind: 'video', src: VIDEO_SRC },
    },
    {
        id: 'cuidado',
        titulo: 'Cuidado com a sua casa',
        texto: 'Antes de abrir a primeira caixa, o piso é protegido. Trabalho limpo, sem arranhões e sem bagunça. Você recebe o móvel pronto e o cômodo do jeito que estava.',
        alt: 'Montagem de móvel com o piso protegido por papelão',
        media: { kind: 'image', src: require('./src/servicos/2.png') },
    },
    {
        id: 'grandes',
        titulo: 'Móveis grandes, montagem precisa',
        texto: 'Roupeiros, painéis e conjuntos com várias portas e gavetas exigem experiência. Alinhamento, nível e ferragens bem fixadas fazem a diferença no uso do dia a dia.',
        alt: 'Roupeiro grande cinza e madeira montado profissionalmente',
        media: { kind: 'image', src: require('./src/servicos/3.png') },
    },
    {
        id: 'acabamento',
        titulo: 'Acabamento que valoriza o móvel',
        texto: 'Portas no prumo, gavetas deslizando sem esforço e visual uniforme. Montagem profissional é o que transforma um kit em um móvel de verdade.',
        alt: 'Guarda-roupa branco e madeira com portas e gavetas alinhadas',
        media: { kind: 'image', src: require('./src/servicos/4.png') },
    },
];

const Servicos_montador_da_017: React.FC = () => {
    const [index, setIndex] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const touchStartX = useRef<number | null>(null);
    const item = SERVICOS[index];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (item.media.kind === 'video') {
            video.play().catch(() => undefined);
        } else {
            video.pause();
        }
    }, [item.media.kind]);

    const goTo = (next: number) => {
        const total = SERVICOS.length;
        setIndex(((next % total) + total) % total);
    };

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) < 40) return;
        goTo(dx > 0 ? index - 1 : index + 1);
    };

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <header className={styles.header}>
                    <Link to="/montador_da_017" className={styles.backButton} aria-label="Voltar">
                        <FaArrowLeft />
                    </Link>
                    <div className={styles.headerText}>
                        <p className={styles.eyebrow}>Montador da 017</p>
                        <h1>Serviços</h1>
                    </div>
                </header>

                <p className={styles.intro}>
                    Montagem profissional de móveis: precisão, cuidado com o ambiente e acabamento de loja na sua
                    casa.
                </p>

                <article className={styles.card}>
                    <div
                        className={styles.carousel}
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                    >
                        <div
                            className={`${styles.viewport} ${
                                item.media.kind === 'video' ? styles.ratioVideo : styles.ratioImage
                            }`}
                        >
                            <div
                                className={styles.track}
                                style={{ transform: `translateX(-${index * 100}%)` }}
                            >
                                {SERVICOS.map((servico) => (
                                    <div key={servico.id} className={styles.slide}>
                                        {servico.media.kind === 'video' ? (
                                            <video
                                                ref={videoRef}
                                                src={servico.media.src}
                                                playsInline
                                                muted
                                                loop
                                                preload="metadata"
                                                width={1080}
                                                height={1920}
                                                aria-label={servico.alt}
                                            >
                                                Seu navegador não suporta reprodução de vídeo.
                                            </video>
                                        ) : (
                                            <img src={servico.media.src} alt={servico.alt} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`${styles.carouselBtn} ${styles.prevBtn}`}
                            onClick={() => goTo(index - 1)}
                            aria-label="Serviço anterior"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            type="button"
                            className={`${styles.carouselBtn} ${styles.nextBtn}`}
                            onClick={() => goTo(index + 1)}
                            aria-label="Próximo serviço"
                        >
                            <FaChevronRight />
                        </button>

                        <div className={styles.indicators}>
                            {SERVICOS.map((servico, idx) => (
                                <button
                                    key={servico.id}
                                    type="button"
                                    className={`${styles.dot} ${idx === index ? styles.active : ''}`}
                                    onClick={() => goTo(idx)}
                                    aria-label={`Ir para ${servico.titulo}`}
                                    aria-current={idx === index}
                                />
                            ))}
                        </div>
                    </div>

                    <div className={styles.cardBody}>
                        <h2>{item.titulo}</h2>
                        <p>{item.texto}</p>
                        <a
                            href={WHATSAPP_URL}
                            className={styles.whatsappBtn}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <WhatsappIcon height="22" color="#ffffff" />
                            Solicitar orçamento
                        </a>
                    </div>
                </article>
            </div>
        </div>
    );
};

export default Servicos_montador_da_017;
